import React from 'react'
import Table, { ColumnsType } from 'antd/es/table'
import { Button, Empty, Space, Tag, Typography } from 'antd'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { horsesApi } from 'api/horses'
import { MainTitle, PageWrapper } from 'components/ui'
import { EHorseStatus, IPopulatedHorseWithoutCount } from 'models/horse'
import { getHorseStatusView, getRepublishMessage, horseStatusViewFilters } from 'shared/constants/horses'
import { getHorseDaysLeft } from 'shared/utils/getHorseDaysLeft'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'

// клик по кнопке в строке не должен открывать карточку
const INTERACTIVE_CELL_SELECTOR = '.ant-btn'

export const MyHorses: React.FC = () => {
  const navigate = useNavigate()

  const [horses, setHorses] = React.useState<IPopulatedHorseWithoutCount[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [republishingId, setRepublishingId] = React.useState<string | null>(null)

  const getHorses = async () => {
    try {
      setIsLoading(true)
      const response = await horsesApi.getMyHorses()
      setHorses(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getHorses()
  }, [])

  const republish = async (horseId: string) => {
    try {
      setRepublishingId(horseId)
      const { data } = await horsesApi.republishHorse(horseId)
      showMessage('success', getRepublishMessage(data.status), '')
      getHorses()
    } catch (e) {
      showServerError(e)
    } finally {
      setRepublishingId(null)
    }
  }

  const columns: ColumnsType<IPopulatedHorseWithoutCount> = React.useMemo(
    () => [
      {
        title: 'Номер',
        dataIndex: 'number',
        width: 101
      },
      {
        title: 'Дата',
        dataIndex: 'createdAt',
        width: 140,
        render: (_, { createdAt }) => (
          <Typography.Text>{moment(createdAt).format('DD.MM.YYYY')}</Typography.Text>
        )
      },
      {
        title: 'Лошадь',
        dataIndex: 'name',
        width: 220
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: 192,
        filters: horseStatusViewFilters,
        onFilter: (value, horse) => getHorseStatusView(horse).label === value,
        render: (_, horse) => {
          const daysLeft = getHorseDaysLeft(horse.published_until)
          const view = getHorseStatusView(horse)
          return (
            <Space direction='vertical' size={4} align='start'>
              <Tag color={view.color}>{view.label}</Tag>
              {horse.status === EHorseStatus.PUBLISHED && daysLeft !== null && (
                <Tag color={daysLeft > 7 ? 'blue' : 'orange'}>
                  Осталось дней: {daysLeft}
                </Tag>
              )}
              {view.note && <Tag color='orange'>{view.note}</Tag>}
            </Space>
          )
        }
      },
      {
        title: 'Действия',
        dataIndex: 'actions',
        width: 200,
        render: (_, { _id, status }) => {
          if (status !== EHorseStatus.ARCHIVED) return null
          return (
            <Button
              type='primary'
              loading={republishingId === _id}
              onClick={() => republish(_id)}
            >
              Разместить заново
            </Button>
          )
        }
      }
    ],
    [republishingId]
  )

  return (
    <PageWrapper>
      <MainTitle text='Мои лошади' />
      <Table
        scroll={{ x: 985 }}
        columns={columns}
        dataSource={horses}
        loading={isLoading}
        rowKey={(el) => el._id}
        onRow={({ _id }) => ({
          onClick: (e) => {
            if ((e.target as HTMLElement).closest(INTERACTIVE_CELL_SELECTOR)) return
            navigate(`/horses/${_id}`)
          },
          style: { cursor: 'pointer' }
        })}
        rowClassName='tableRow'
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='Лошади добавляются в мобильном приложении HorseSmart'
            />
          )
        }}
      />
    </PageWrapper>
  )
}
